import * as THREE from 'three';

function hash(x, y, z) {
  const h = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
  return h - Math.floor(h);
}

export function displaceGeometry(geo, scale = 1.0, seed = 0) {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const n = hash(x + seed, y, z) + hash(x * 0.7, y * 1.3, z * 0.5 + seed) - 1.0;
    const dx = n * scale;
    const dz = n * scale * 0.9;
    const dy = (hash(x * 0.6, y * 0.4, z * 0.5 + seed) - 0.5) * scale * 0.5;
    pos.setXYZ(i, x + dx, y + dy, z + dz);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

// Shared materials — referenced across all islands. Three.js batches meshes
// sharing the same material, dropping draw calls dramatically.
export const MAT = {
  rock:       new THREE.MeshStandardMaterial({ color: 0x6b4f3a, roughness: 0.95 }),
  rockShadow: new THREE.MeshStandardMaterial({ color: 0x3d2818, roughness: 1.0 }),
  grass:      new THREE.MeshStandardMaterial({ color: 0x7a9844, roughness: 0.85 }),
  grassEdge:  new THREE.MeshStandardMaterial({ color: 0xb0c870, roughness: 0.8 }),
  pedestal:   new THREE.MeshStandardMaterial({ color: 0x55483a, roughness: 0.7 })
};

export function islandBase(radius = 18, height = 14, seed = 0) {
  const g = new THREE.Group();

  // Main cliff — wider top, dramatic taper to a point
  const rockGeo = new THREE.CylinderGeometry(radius * 1.05, radius * 0.12, height, 14, 5);
  displaceGeometry(rockGeo, 3.0, seed);
  const rock = new THREE.Mesh(rockGeo, MAT.rock);
  rock.position.y = -height / 2;
  g.add(rock);

  // Dangling tip below — gives that floating-island look
  const tipGeo = new THREE.ConeGeometry(radius * 0.22, height * 0.55, 8);
  displaceGeometry(tipGeo, 1.4, seed + 5);
  const tip = new THREE.Mesh(tipGeo, MAT.rockShadow);
  tip.position.y = -height - height * 0.22;
  tip.rotation.y = hash(seed, 1, 2) * Math.PI;
  g.add(tip);

  // Grass cap — slightly wider than rock top, undulating surface
  const grassGeo = new THREE.CylinderGeometry(radius * 1.08, radius * 1.02, 3, 16);
  displaceGeometry(grassGeo, 1.0, seed + 11);
  const grass = new THREE.Mesh(grassGeo, MAT.grass);
  grass.position.y = 1.5;
  g.add(grass);

  // Bright grass edge ring (catches sunlight, breaks up the silhouette)
  const edgeGeo = new THREE.TorusGeometry(radius * 1.04, 0.7, 6, 18);
  const edge = new THREE.Mesh(edgeGeo, MAT.grassEdge);
  edge.rotation.x = Math.PI / 2;
  edge.position.y = 2.8;
  g.add(edge);

  return g;
}

// Small stone pedestal under topic features so they feel like installations on
// the island, not floating diagrams. Use this in island factories.
export function pedestal(radius = 4, height = 1.8) {
  const geo = new THREE.CylinderGeometry(radius * 0.85, radius, height, 8);
  const m = new THREE.Mesh(geo, MAT.pedestal);
  m.position.y = height / 2 + 2.4;
  return m;
}

// One InstancedMesh per detail type, scattering grass and boulders across every
// non-floating island. Single draw call for hundreds of details.
export function buildGroundDetail(scene, islandObjects) {
  // Skip quantum (floating crystal — no terrain) and profile (has its own garden details)
  const candidates = islandObjects.filter(o => o.project.topic !== 'quantum' && o.project.id !== 'profile');

  // Grass tufts
  const grassGeo = new THREE.ConeGeometry(0.5, 1.7, 4);
  const grassMat = new THREE.MeshStandardMaterial({ color: 0x8aa84e, roughness: 0.9 });
  const PER_GRASS = 14;
  const grass = new THREE.InstancedMesh(grassGeo, grassMat, candidates.length * PER_GRASS);

  // Boulders
  const boulderGeo = new THREE.DodecahedronGeometry(1.1, 0);
  const boulderMat = new THREE.MeshStandardMaterial({ color: 0x7a6048, roughness: 0.92 });
  const PER_BOULDER = 3;
  const boulders = new THREE.InstancedMesh(boulderGeo, boulderMat, candidates.length * PER_BOULDER);

  const dummy = new THREE.Object3D();
  let gi = 0, bi = 0;
  candidates.forEach((o, idx) => {
    const [x, y, z] = o.project.pos;
    const baseR = 14;
    const seed = idx * 17;

    for (let i = 0; i < PER_GRASS; i++) {
      const angle = (i / PER_GRASS) * Math.PI * 2 + hash(seed + i, 1, 0) * 0.6;
      const dist = baseR * (0.3 + hash(seed + i, 2, 0) * 0.62);
      dummy.position.set(
        x + Math.cos(angle) * dist,
        y + 3.2,
        z + Math.sin(angle) * dist
      );
      dummy.rotation.set(0, hash(seed + i, 3, 0) * Math.PI, 0);
      dummy.scale.setScalar(0.55 + hash(seed + i, 4, 0) * 0.55);
      dummy.updateMatrix();
      grass.setMatrixAt(gi++, dummy.matrix);
    }

    for (let i = 0; i < PER_BOULDER; i++) {
      const angle = hash(seed + i, 5, 0) * Math.PI * 2;
      const dist = baseR * (0.55 + hash(seed + i, 6, 0) * 0.3);
      dummy.position.set(
        x + Math.cos(angle) * dist,
        y + 2.8 + hash(seed + i, 7, 0) * 0.3,
        z + Math.sin(angle) * dist
      );
      dummy.rotation.set(
        hash(seed + i, 8, 0) * Math.PI,
        hash(seed + i, 9, 0) * Math.PI * 2,
        hash(seed + i, 10, 0) * Math.PI
      );
      dummy.scale.setScalar(0.5 + hash(seed + i, 11, 0) * 0.8);
      dummy.updateMatrix();
      boulders.setMatrixAt(bi++, dummy.matrix);
    }
  });
  grass.count = gi;
  boulders.count = bi;
  grass.instanceMatrix.needsUpdate = true;
  boulders.instanceMatrix.needsUpdate = true;

  scene.add(grass);
  scene.add(boulders);
}
